import { inject } from '@angular/core';
import { CanMatchFn, Router } from '@angular/router';
import { Role } from '../../models/enums';
import { AuthService } from '../auth/auth.service';
import { MAINTENANCE_MODE } from './maintenance.config';

/**
 * Guard de maintenance global — implémenté en CanMatchFn.
 *
 * CanMatch s'exécute pendant la phase de reconnaissance de route, AVANT les canActivate.
 * Cela garantit que authGuard (qui utilise router.navigate() de façon impérative)
 * ne s'exécute jamais quand la maintenance intercepte — plus de conflit de navigation.
 * Bonus : sur les routes lazy, le bundle n'est pas chargé du tout si la maintenance bloque.
 *
 * Quand MAINTENANCE_MODE = true :
 *   - WA_ADMIN → true (route reconnue, accès complet)
 *   - Tous les autres → UrlTree('/maintenance') (route non reconnue, redirection)
 * Quand MAINTENANCE_MODE = false → true (transparent, comportement normal)
 *
 * Pour désactiver la maintenance : mettre MAINTENANCE_MODE = false dans maintenance.config.ts.
 */
export const maintenanceGuard: CanMatchFn = () => {
  if (!MAINTENANCE_MODE) return true;

  const authService = inject(AuthService);
  const router = inject(Router);

  // Pas d'attente ici : les rôles sont chargés avant la première navigation par
  // provideSessionBootstrap (APP_INITIALIZER), donc un WA_ADMIN n'est plus redirigé
  // à tort au premier chargement (F5).

  // WA_ADMIN explicite requis — hasRole ne bypass pas via SUPER_ADMIN
  if (authService.hasRole([Role.WaAdmin])) return true;

  return router.createUrlTree(['/maintenance']);
};

/**
 * Guard de la page /maintenance elle-même — symétrique de maintenanceGuard.
 * Maintenance active → page accessible (c'est la destination de redirection).
 * Maintenance inactive → redirige vers / pour ne laisser personne bloqué sur
 * la page (onglet ouvert pendant la maintenance, favori, lien partagé...).
 */
export const maintenancePageGuard: CanMatchFn = () => {
  if (MAINTENANCE_MODE) return true;
  return inject(Router).createUrlTree(['/']);
};
